"use client";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { useEffect, useRef, type ReactNode } from "react";
import { useCurrentUserId } from "@/hooks/use-current-user-id";

function PostHogIdentify() {
  const userId = useCurrentUserId();
  const previousId = useRef<string | null>(null);

  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
    if (!userId) {
      if (previousId.current) posthog.reset();
      previousId.current = userId;
      return;
    }

    if (previousId.current && previousId.current !== userId) {
      posthog.reset();
    }
    previousId.current = userId;

    let cancelled = false;
    (async () => {
      let scope: {
        effectiveRole?: string;
        actorRole?: string;
        schoolId?: string | null;
        schoolName?: string | null;
        impersonating?: boolean;
      } | null = null;
      try {
        const res = await fetch("/api/me/scope", { credentials: "include" });
        if (res.ok) scope = await res.json();
      } catch {
        scope = null;
      }
      if (cancelled) return;

      posthog.identify(userId, {
        role: scope?.effectiveRole,
        actor_role: scope?.actorRole,
        school_id: scope?.schoolId ?? undefined,
        school_name: scope?.schoolName ?? undefined,
        impersonating: scope?.impersonating,
      });

      posthog.register({
        scope: scope?.effectiveRole === "SCHOOL" ? "school" : "internal",
        role: scope?.effectiveRole,
        school_id: scope?.schoolId ?? undefined,
        school_name: scope?.schoolName ?? undefined,
        impersonating: scope?.impersonating,
      });

      if (scope?.schoolId) {
        posthog.group("school", scope.schoolId, {
          name: scope.schoolName ?? undefined,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  return null;
}

export function PostHogProvider({ children }: { children: ReactNode }) {
  return (
    <PHProvider client={posthog}>
      <PostHogIdentify />
      {children}
    </PHProvider>
  );
}
